import axios from 'axios';

import { config } from '../config';
import { getMosAccessToken, isMosLoginConfigured } from '@services/mosAuth';
import { isMosAuthProvider } from '@commons/auth/session';

const getMosConfig = () => config.mos || {};

const getMosAdminApiHost = () => getMosConfig().adminApiHost;

/**
 * The switchers call the MOS admin API with the user's MOS access token, so they only
 * make sense for sessions authenticated through Login with MOS. Legacy sessions
 * (Cryptr, email/password, LoopBack token) have no MOS token and must not display them.
 */
export const canUseMosSwitchers = () => {
  if (!isMosLoginConfigured()) {
    return false;
  }

  if (!isMosAuthProvider()) {
    return false;
  }

  return Boolean(getMosConfig().adminApiHost);
};

export const hasMosSwitchersAccess = (user) => {
  if (!user?.mosUserId) {
    return false;
  }

  return canUseMosSwitchers();
};

/**
 * All pages are fetched since the
 * sidebar lists every operator instead of a searchable dialog.
 */
const MOS_OPERATORS_PAGE_SIZE = 20;

const getMosAdminApiAuthHeaders = async () => {
  const accessToken = await getMosAccessToken();

  return { Authorization: `Bearer ${accessToken}` };
};

/**
 * Lists the MOS operators the user has access to (MOS admin API, same contract as the
 * MOS operator switcher data service), sorted by name.
 *
 * @return {Promise<{ id: string, name: string }[]>}
 */
export const fetchMosOperators = async () => {
  const adminApiHost = getMosAdminApiHost();
  const headers = await getMosAdminApiAuthHeaders();

  const operators = [];
  let currentPage = 1;
  let totalCount = 0;

  do {
    const { data } = await axios.get(`${adminApiHost}/v1/user/operators`, {
      headers,
      params: { currentPage, pageSize: MOS_OPERATORS_PAGE_SIZE },
    });

    const pageOperators = data?.operators ?? [];

    if (!pageOperators.length) {
      break;
    }

    operators.push(...pageOperators);
    totalCount = data?.totalCount ?? 0;
    currentPage += 1;
  } while (operators.length < totalCount);

  return operators.sort((operatorA, operatorB) =>
    (operatorA.name ?? '').localeCompare(operatorB.name ?? ''),
  );
};

/**
 * Lists the MOS products (applications) of the user, in the order MOS returns them,
 * same contract as the MOS product switcher data service.
 *
 * @return {Promise<{ code: string, name: string, url: string|null, status: string }[]>}
 */
export const fetchMosProducts = async () => {
  const adminApiHost = getMosAdminApiHost();
  const headers = await getMosAdminApiAuthHeaders();

  const { data } = await axios.get(`${adminApiHost}/user/applications`, { headers });

  return data?.applications ?? [];
};

/**
 * Persists the default operator on the MOS side; the next MOS access token issued
 * carries it as its operator_id claim.
 */
export const switchMosDefaultOperator = async (operatorId) => {
  const adminApiHost = getMosAdminApiHost();
  const headers = await getMosAdminApiAuthHeaders();

  await axios.put(
    `${adminApiHost}/v1/user/default-operator/${encodeURIComponent(operatorId)}`,
    {},
    { headers },
  );
};
